import React from 'react';
import {
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
  Box,
} from '@mui/material';
import { SearchResult, MatchInfo } from '../hooks/useSearch';

interface SearchResultsProps {
  results: SearchResult[];
  query: string;
  onSelect: (result: SearchResult) => void;
  onClose: () => void;
  /** Shown when regex mode is on and the pattern is invalid. */
  regexInvalid?: boolean;
}

/** Highlights a single [start,end) span (used for note snippets). */
function highlightSpan(
  text: string,
  start: number,
  end: number,
): React.ReactNode {
  if (end <= start) return text;
  return (
    <>
      {text.slice(0, start)}
      <Box
        component="span"
        sx={{
          backgroundColor: 'rgba(255, 213, 79, 0.4)',
          borderRadius: '2px',
          px: '1px',
        }}
      >
        {text.slice(start, end)}
      </Box>
      {text.slice(end)}
    </>
  );
}

function highlightText(
  text: string,
  indices: [number, number][],
): React.ReactNode {
  if (!indices || indices.length === 0) {
    return text;
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  const sorted = [...indices].sort((a, b) => a[0] - b[0]);

  for (const [start, end] of sorted) {
    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }
    parts.push(
      <Box
        component="span"
        key={start}
        sx={{
          backgroundColor: 'rgba(255, 213, 79, 0.4)',
          borderRadius: '2px',
          px: '1px',
        }}
      >
        {text.slice(start, end + 1)}
      </Box>,
    );
    lastIndex = end + 1;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <>{parts}</>;
}

function getNoteSnippet(
  match: MatchInfo,
  contextChars: number = 40,
): React.ReactNode {
  const { value, indices } = match;
  if (!indices || indices.length === 0 || !value) return null;

  const firstMatch = indices[0];
  const lastMatch = indices[indices.length - 1];

  const snippetStart = Math.max(0, firstMatch[0] - contextChars);
  const snippetEnd = Math.min(value.length, lastMatch[1] + 1 + contextChars);

  const snippet = value.slice(snippetStart, snippetEnd);
  const prefix = snippetStart > 0 ? '…' : '';
  const suffix = snippetEnd < value.length ? '…' : '';

  // Adjust indices relative to the snippet
  const adjustedIndices: [number, number][] = indices
    .filter(([s, e]) => s >= snippetStart && e < snippetEnd)
    .map(([s, e]) => [s - snippetStart, e - snippetStart] as [number, number]);

  return (
    <Typography
      component="span"
      variant="body2"
      color="text.secondary"
      sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}
    >
      {prefix}
      {highlightText(snippet, adjustedIndices)}
      {suffix}
    </Typography>
  );
}

function getMatchesForKey(
  matches: MatchInfo[],
  key: string,
): MatchInfo | undefined {
  return matches.find((m) => m.key === key);
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  query,
  onSelect,
  onClose,
  regexInvalid,
}) => {
  if (!query) {
    return null;
  }

  if (regexInvalid) {
    return (
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: '100%',
          mt: 0.5,
          left: 0,
          zIndex: 10,
          width: '100%',
          maxHeight: 400,
          overflow: 'auto',
        }}
      >
        <Typography sx={{ p: 2 }} color="error" variant="body2">
          Invalid regular expression.
        </Typography>
      </Paper>
    );
  }

  if (results.length === 0) {
    return (
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: '100%',
          mt: 0.5,
          left: 0,
          zIndex: 10,
          width: '100%',
          maxHeight: 400,
          overflow: 'auto',
        }}
      >
        <Typography sx={{ p: 2, textAlign: 'center' }} color="text.secondary">
          No results found
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        top: '100%',
        mt: 0.5,
        left: 0,
        zIndex: 10,
        width: '100%',
        maxHeight: 400,
        overflow: 'auto',
      }}
    >
      <List disablePadding>
        {results.map((result) => {
          if (result.kind === 'note') {
            return (
              <ListItemButton
                key={`note-${result.note.note_id}`}
                onClick={() => onSelect(result)}
              >
                <ListItemText
                  primary={
                    <Typography component="span" sx={{ fontWeight: 'bold' }}>
                      📝 {result.note.note_title}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}
                      >
                        {highlightSpan(
                          result.snippet,
                          result.matchStart,
                          result.matchEnd,
                        )}
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                      >
                        {result.categoryPath}
                      </Typography>
                    </>
                  }
                />
              </ListItemButton>
            );
          }

          const nameMatch = getMatchesForKey(result.matches, 'bookmark_name');
          const urlMatch = getMatchesForKey(result.matches, 'bookmark_url');
          const noteMatch = getMatchesForKey(result.matches, 'notes_plain');

          return (
            <ListItemButton
              key={`bm-${result.bookmark.bookmark_id}`}
              onClick={() => onSelect(result)}
            >
              <ListItemText
                primary={
                  <Typography component="span" sx={{ fontWeight: 'bold' }}>
                    {nameMatch
                      ? highlightText(
                          result.bookmark.bookmark_name,
                          nameMatch.indices,
                        )
                      : result.bookmark.bookmark_name}
                  </Typography>
                }
                secondary={
                  <>
                    {urlMatch && (
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        sx={{ display: 'block' }}
                      >
                        {highlightText(
                          result.bookmark.bookmark_url,
                          urlMatch.indices,
                        )}
                      </Typography>
                    )}
                    {noteMatch && getNoteSnippet(noteMatch)}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      {result.categoryPath}
                    </Typography>
                  </>
                }
              />
            </ListItemButton>
          );
        })}
      </List>
    </Paper>
  );
};
